import React, { useEffect, useState, useRef } from 'react';
import DesktopGridAd from '../DesktopGridAd/DesktopGridAd';
import MobileGridAd from '../MobileGridAd/MobileGridAd';
import AdMobInFeedAd from '../AdMobInFeedAd/AdMobInFeedAd';
import { isAndroidCapacitor } from '../../utils/platformUtils';
import {
    DESKTOP_GRID_AD_SLOT,
    DESKTOP_GRID_3COL_MQ,
    DESKTOP_GRID_4COL_MQ,
    DESKTOP_GRID_MIN_MQ,
    MOBILE_GRID_AD_ROW_INTERVAL,
    MOBILE_GRID_AD_SLOT,
    buildDesktopGridCells,
    getDesktopGridColumnCount,
    getFeedGridAdRowInterval,
    isDesktopGridBreakpoint,
    shouldShowFeedGridAds,
    shouldShowWebGridAds,
} from '../../utils/desktopGridAds';
import './InfiniteFeed.css';

export function FeedSkeleton({ count = 12 }) {
    return (
        <div className="infinite-feed-grid">
            {Array.from({ length: count }).map((_, index) => (
                <div key={index} className="feed-skeleton-card" />
            ))}
        </div>
    );
}

function useFeedGridAdLayout() {
    const [enabled, setEnabled] = useState(shouldShowFeedGridAds);
    const [isDesktop, setIsDesktop] = useState(isDesktopGridBreakpoint);
    const [isNative, setIsNative] = useState(() => isAndroidCapacitor?.() === true);
    const [columns, setColumns] = useState(getDesktopGridColumnCount);

    useEffect(() => {
        if (typeof window === 'undefined') return undefined;

        const mqMin = window.matchMedia(DESKTOP_GRID_MIN_MQ);
        const mq3 = window.matchMedia(DESKTOP_GRID_3COL_MQ);
        const mq4 = window.matchMedia(DESKTOP_GRID_4COL_MQ);

        const update = () => {
            setEnabled(shouldShowFeedGridAds());
            setIsDesktop(isDesktopGridBreakpoint());
            setIsNative(isAndroidCapacitor?.() === true);
            setColumns(getDesktopGridColumnCount());
        };

        update();
        mqMin.addEventListener('change', update);
        mq3.addEventListener('change', update);
        mq4.addEventListener('change', update);
        return () => {
            mqMin.removeEventListener('change', update);
            mq3.removeEventListener('change', update);
            mq4.removeEventListener('change', update);
        };
    }, []);

    return { enabled, isDesktop, isNative, columns };
}

export default function InfiniteFeed({
    items,
    renderItem,
    hasMore,
    loading,
    loadingMore,
    onLoadMore,
    emptyMessage = 'No items found',
    getKey,
    gridAdSlot = DESKTOP_GRID_AD_SLOT,
    mobileGridAdSlot = MOBILE_GRID_AD_SLOT,
    gridAdRowInterval,
    mobileGridAdRowInterval = MOBILE_GRID_AD_ROW_INTERVAL,
}) {
    const sentinelRef = useRef(null);
    const emptyAdvanceRef = useRef(false);
    const { enabled, isDesktop, isNative, columns } = useFeedGridAdLayout();
    const webAdsOk = shouldShowWebGridAds();
    const showGridAds =
        enabled &&
        (isNative || (webAdsOk && Boolean(isDesktop ? gridAdSlot : mobileGridAdSlot)));
    const rowInterval = isDesktop
        ? (gridAdRowInterval ?? getFeedGridAdRowInterval(true))
        : (mobileGridAdRowInterval ?? getFeedGridAdRowInterval(false));

    useEffect(() => {
        const el = sentinelRef.current;
        if (!el) return undefined;
        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0]?.isIntersecting && hasMore && !loading && !loadingMore) {
                    onLoadMore?.();
                }
            },
            { rootMargin: '1000px 0px' }
        );
        observer.observe(el);
        return () => observer.disconnect();
    }, [hasMore, loading, loadingMore, onLoadMore, items.length]);

    // Filtered Dynamo pages can return 0 rows with a next token — keep fetching
    // instead of showing an empty state while more pages exist.
    useEffect(() => {
        if (items.length > 0 || !hasMore || loading || loadingMore) {
            emptyAdvanceRef.current = false;
            return;
        }
        if (emptyAdvanceRef.current) return;
        emptyAdvanceRef.current = true;
        onLoadMore?.();
    }, [items.length, hasMore, loading, loadingMore, onLoadMore]);

    if ((loading || loadingMore || hasMore) && items.length === 0) {
        return <FeedSkeleton />;
    }

    if (items.length === 0) {
        return (
            <div className="infinite-feed-empty">
                <p className="infinite-feed-empty-title">{emptyMessage}</p>
                <p className="infinite-feed-empty-hint">Loosen a filter or run a fresh search lap.</p>
            </div>
        );
    }

    const cells = buildDesktopGridCells(items, {
        columns,
        rowInterval,
        enabled: showGridAds,
    });

    return (
        <>
            <div className="infinite-feed-grid">
                {cells.map((cell) => {
                    if (cell.type === 'ad') {
                        return (
                            <div key={cell.key} className="infinite-feed-ad-row">
                                {isNative ? (
                                    <AdMobInFeedAd slotKey={cell.key} />
                                ) : isDesktop ? (
                                    <DesktopGridAd slot={gridAdSlot} />
                                ) : (
                                    <MobileGridAd slot={mobileGridAdSlot} />
                                )}
                            </div>
                        );
                    }

                    const { item, index } = cell;
                    return (
                        <div
                            key={getKey ? getKey(item, index) : item.id || index}
                            className="infinite-feed-card"
                        >
                            {renderItem(item, index)}
                        </div>
                    );
                })}
            </div>
            {loadingMore && <div className="infinite-feed-more">Loading more...</div>}
            <div ref={sentinelRef} className="infinite-feed-sentinel" />
        </>
    );
}
